import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { Button } from "../../../../components/common/button";
import { showErrorToast } from "../../../../utils/toasts";
import { AuthDialog } from "../../../authentication/presentation/components/auth-dialog";
import { useCategories } from "../../../products/hooks/useCategories";
import { AiChatPanel } from "../../../ai-search/presentation/components/ai-chat-panel";
import {
  categoryImages,
  ctaWaves,
  featureIcons,
  smartRewardsWaves,
  stepsWaves,
  whyBackgrounds,
  whyBadges,
} from "./landing-assets";
import "./landing-page.css";

export interface LandingPageProps {
  /** When true, hides the top nav + marketing sections + footer (used inside the buyer shell). */
  embedded?: boolean;
  /** Optional content injected between the hero and the "Shop by category" section. */
  afterHero?: ReactNode;
  /** Called with the query value when the user submits a search. */
  onSearch?: (query: string) => void;
  /**
   * Submitted once on mount exactly as if the user had typed it and pressed
   * Ask — used for a query handed over from the header search. Later prop
   * changes are ignored; remount the page to submit another one.
   */
  initialQuery?: string;
  /** Restores the AI results panel and scroll lock when returning from compare. */
  restoreResultsOpen?: boolean;
  /** Syncs results-panel open state to the route/history. */
  onResultsOpenChange?: (open: boolean) => void;
  /** Full chat history from `useChat`. Messages[0..1] represent the first
   *  turn and are visualised by the recommendations panel — anything beyond
   *  those is rendered as chat bubbles below the panel. */
  chatTranscript?: import("../../../ai-search/data/types/chat").ChatMessage[];
  /** Whether the assistant is currently streaming the latest reply. */
  chatResponding?: boolean;
  /** Callback to re-send the last failed message. Null when nothing to retry. */
  chatRetry?: (() => void) | null;
  /** Aborts the in-flight streaming request. */
  chatStopGenerating?: () => void;
  /** Sends a message programmatically — used by interactive widgets (follow-up questions, refine filters). */
  chatSendMessage?: (text: string) => void;
}

const INTELLIGENT_CARD_SVG = `
<svg viewBox="0 0 302 382" overflow="visible" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0;width:100%;height:100%;">
  <g opacity="0.65">
    <line x1="-42" y1="10"  x2="-20" y2="28"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-42" y1="10"  x2="-14" y2="-8"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-14" y1="-8"  x2="8"   y2="18"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-20" y1="28"  x2="-14" y2="42"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-20" y1="28"  x2="8"   y2="18"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-32" y1="55"  x2="-20" y2="28"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-32" y1="55"  x2="-14" y2="42"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-14" y1="42"  x2="20"  y2="62"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-14" y1="42"  x2="8"   y2="18"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="8"   y1="18"  x2="48"  y2="40"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="48"  y1="40"  x2="20"  y2="62"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="48"  y1="40"  x2="74"  y2="56"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="20"  y1="62"  x2="40"  y2="82"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="40"  y1="82"  x2="74"  y2="56"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="74"  y1="56"  x2="96"  y2="72"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="40"  y1="82"  x2="64"  y2="96"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="64"  y1="96"  x2="96"  y2="72"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="-14" y1="-8"  x2="22"  y2="-18" stroke="#a0c8e4" stroke-width="1"/>
    <line x1="22"  y1="-18" x2="52"  y2="8"   stroke="#a0c8e4" stroke-width="1"/>
    <line x1="52"  y1="8"   x2="8"   y2="18"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="52"  y1="8"   x2="48"  y2="40"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="96"  y1="72"  x2="120" y2="48"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="120" y1="48"  x2="148" y2="66"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="148" y1="66"  x2="130" y2="90"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="130" y1="90"  x2="96"  y2="72"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="120" y1="48"  x2="152" y2="32"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="152" y1="32"  x2="148" y2="66"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="152" y1="32"  x2="180" y2="50"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="180" y1="50"  x2="148" y2="66"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="180" y1="50"  x2="200" y2="30"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="200" y1="30"  x2="228" y2="22"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="180" y1="50"  x2="200" y2="72"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="200" y1="72"  x2="228" y2="22"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="96"  y1="72"  x2="228" y2="22"  stroke="#a0c8e4" stroke-width="1"/>
    <circle cx="-42" cy="10"  r="5"   fill="#6bb5df"/>
    <circle cx="-20" cy="28"  r="6"   fill="#6bb5df"/>
    <circle cx="-14" cy="-8"  r="4.5" fill="#6bb5df"/>
    <circle cx="-32" cy="55"  r="5"   fill="#6bb5df"/>
    <circle cx="-14" cy="42"  r="5.5" fill="#6bb5df"/>
    <circle cx="8"   cy="18"  r="5"   fill="#6bb5df"/>
    <circle cx="22"  cy="-18" r="4"   fill="#6bb5df"/>
    <circle cx="52"  cy="8"   r="5.5" fill="#6bb5df"/>
    <circle cx="20"  cy="62"  r="7"   fill="#6bb5df"/>
    <circle cx="48"  cy="40"  r="6"   fill="#6bb5df"/>
    <circle cx="40"  cy="82"  r="5.5" fill="#6bb5df"/>
    <circle cx="74"  cy="56"  r="7.5" fill="#6bb5df"/>
    <circle cx="64"  cy="96"  r="5"   fill="#6bb5df"/>
    <circle cx="96"  cy="72"  r="6"   fill="#6bb5df"/>
    <circle cx="120" cy="48"  r="6"   fill="#6bb5df"/>
    <circle cx="130" cy="90"  r="5"   fill="#6bb5df"/>
    <circle cx="148" cy="66"  r="7"   fill="#6bb5df"/>
    <circle cx="152" cy="32"  r="5.5" fill="#6bb5df"/>
    <circle cx="180" cy="50"  r="7"   fill="#6bb5df"/>
    <circle cx="200" cy="30"  r="5.5" fill="#6bb5df"/>
    <circle cx="200" cy="72"  r="5"   fill="#6bb5df"/>
  </g>
  <g opacity="0.65">
    <line x1="228" y1="22"  x2="258" y2="40"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="258" y1="40"  x2="288" y2="18"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="288" y1="18"  x2="315" y2="-5"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="315" y1="-5"  x2="322" y2="38"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="322" y1="38"  x2="288" y2="18"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="322" y1="38"  x2="305" y2="68"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="305" y1="68"  x2="272" y2="65"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="272" y1="65"  x2="258" y2="40"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="258" y1="40"  x2="228" y2="22"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="305" y1="68"  x2="318" y2="108" stroke="#a0c8e4" stroke-width="1"/>
    <line x1="272" y1="65"  x2="298" y2="130" stroke="#a0c8e4" stroke-width="1"/>
    <line x1="318" y1="108" x2="298" y2="130" stroke="#a0c8e4" stroke-width="1"/>
    <line x1="315" y1="-5"  x2="338" y2="-22" stroke="#a0c8e4" stroke-width="1"/>
    <line x1="338" y1="-22" x2="355" y2="12"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="355" y1="12"  x2="322" y2="38"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="355" y1="12"  x2="340" y2="55"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="340" y1="55"  x2="322" y2="38"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="340" y1="55"  x2="350" y2="88"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="350" y1="88"  x2="318" y2="108" stroke="#a0c8e4" stroke-width="1"/>
    <line x1="338" y1="-22" x2="318" y2="-38" stroke="#a0c8e4" stroke-width="1"/>
    <line x1="318" y1="-38" x2="290" y2="-18" stroke="#a0c8e4" stroke-width="1"/>
    <line x1="290" y1="-18" x2="315" y2="-5"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="290" y1="-18" x2="268" y2="2"   stroke="#a0c8e4" stroke-width="1"/>
    <line x1="268" y1="2"   x2="288" y2="18"  stroke="#a0c8e4" stroke-width="1"/>
    <line x1="268" y1="2"   x2="228" y2="22"  stroke="#a0c8e4" stroke-width="1"/>
    <circle cx="228" cy="22"  r="6"   fill="#6bb5df"/>
    <circle cx="258" cy="40"  r="8"   fill="#6bb5df"/>
    <circle cx="268" cy="2"   r="5"   fill="#6bb5df"/>
    <circle cx="288" cy="18"  r="5.5" fill="#6bb5df"/>
    <circle cx="290" cy="-18" r="5"   fill="#6bb5df"/>
    <circle cx="315" cy="-5"  r="6"   fill="#6bb5df"/>
    <circle cx="318" cy="-38" r="4.5" fill="#6bb5df"/>
    <circle cx="322" cy="38"  r="7"   fill="#6bb5df"/>
    <circle cx="338" cy="-22" r="5"   fill="#6bb5df"/>
    <circle cx="340" cy="55"  r="6"   fill="#6bb5df"/>
    <circle cx="350" cy="88"  r="5"   fill="#6bb5df"/>
    <circle cx="355" cy="12"  r="6.5" fill="#6bb5df"/>
    <circle cx="272" cy="65"  r="6"   fill="#6bb5df"/>
    <circle cx="305" cy="68"  r="8"   fill="#6bb5df"/>
    <circle cx="318" cy="108" r="5.5" fill="#6bb5df"/>
    <circle cx="298" cy="130" r="5"   fill="#6bb5df"/>
  </g>
  <g opacity="0.65">
    <line x1="318" y1="108" x2="312" y2="340" stroke="#a0c8e4" stroke-width="1"/>
    <circle cx="312" cy="340" r="7" fill="#6bb5df"/>
    <circle cx="320" cy="375" r="5" fill="#6bb5df"/>
    <line x1="312" y1="340" x2="320" y2="375" stroke="#a0c8e4" stroke-width="1"/>
  </g>
</svg>`;

const ARROW_SVG = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d3af37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export default function LandingPage({
  embedded = false,
  afterHero,
  onSearch,
  initialQuery,
  restoreResultsOpen = false,
  onResultsOpenChange,
  chatTranscript,
  chatResponding = false,
  chatRetry,
  chatStopGenerating,
  chatSendMessage,
}: LandingPageProps = {}) {
  const navigate = useNavigate();
  const onSearchRef = useRef(onSearch);
  useEffect(() => { onSearchRef.current = onSearch; }, [onSearch]);
  const initialQueryRef = useRef(initialQuery);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "signup">("login");
  const openAuth = useCallback((tab: "login" | "signup" = "login") => {
    setAuthTab(tab);
    setAuthOpen(true);
  }, []);
  const openAuthRef = useRef(openAuth);
  useEffect(() => { openAuthRef.current = openAuth; }, [openAuth]);

  const [resultsOpen, setResultsOpen] = useState(false);
  // chatDocked drives the search bar's `thinking-lower` class via React, so
  // the className survives re-renders. Imperative classList.add was getting
  // out-of-sync with React's reconciliation (logged-in flow, restoreResultsOpen
  // path on direct URL load).
  const [chatDocked, setChatDocked] = useState(false);
  const setResultsOpenRef = useRef(setResultsOpen);
  useEffect(() => { setResultsOpenRef.current = setResultsOpen; }, []);
  const setChatDockedRef = useRef(setChatDocked);
  useEffect(() => { setChatDockedRef.current = setChatDocked; }, []);
  const resultsOpenRef = useRef(false);
  useEffect(() => { resultsOpenRef.current = resultsOpen; }, [resultsOpen]);
  const onResultsOpenChangeRef = useRef(onResultsOpenChange);
  useEffect(() => { onResultsOpenChangeRef.current = onResultsOpenChange; }, [onResultsOpenChange]);
  const chatRespondingRef = useRef(chatResponding);
  useEffect(() => { chatRespondingRef.current = chatResponding; }, [chatResponding]);
  const chatRetryRef = useRef(chatRetry);
  useEffect(() => { chatRetryRef.current = chatRetry; }, [chatRetry]);
  const chatStopRef = useRef(chatStopGenerating);
  useEffect(() => { chatStopRef.current = chatStopGenerating; }, [chatStopGenerating]);

  // Single source of truth for the chat-mode scroll lock. When the chat is
  // docked the rpPanel becomes the only scroller — locking the body and the
  // AppShell main-container prevents wheel events from being absorbed by the
  // page beneath. This runs identically for the logged-in (restoreResultsOpen)
  // and logged-out (after-thinking) entry paths.
  useEffect(() => {
    if (!chatDocked) return;
    const main = document.getElementById("main-container");
    const prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    main?.classList.add("thinking-scroll-lock");
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      main?.classList.remove("thinking-scroll-lock");
    };
  }, [chatDocked]);

  useEffect(() => {
    if (!restoreResultsOpen) return;

    const overlay = document.getElementById("thinkingOverlay");
    const askBtn = document.getElementById("askBtn") as HTMLButtonElement | null;

    overlay?.removeAttribute("aria-hidden");
    overlay?.classList.add("active", "results-mode");
    document.getElementById("heroText")?.classList.add("thinking-hide");
    document.getElementById("searchHint")?.classList.add("thinking-hide");
    document.getElementById("heroFeatures")?.classList.add("thinking-hide");
    if (askBtn) {
      askBtn.innerHTML = "Ask Procurtal";
      askBtn.classList.remove("stop-mode");
    }
    setChatDocked(true);
    setResultsOpen(true);
  }, [restoreResultsOpen]);

  // Categories are only needed for the authenticated (embedded) experience —
  // fetching them on the public landing would trigger an unauthenticated API
  // call. Map the card's display name to the backend category id so the
  // catalog page can pick up the filter from the URL.
  const { data: categories } = useCategories(embedded);
  const categoryIdByName = useMemo(() => {
    const map = new Map<string, number>();
    (categories ?? []).forEach((cat) => {
      if (cat.name) map.set(cat.name.toLowerCase(), cat.id);
    });
    return map;
  }, [categories]);

  const handleCategoryClick = useCallback(
    (name: string) => {
      if (!embedded) {
        openAuth("login");
        return;
      }
      const id = categoryIdByName.get(name.toLowerCase());
      navigate(id ? `${AppRoutes.PRODUCTS}?categoryIds=${id}` : AppRoutes.PRODUCTS);
    },
    [embedded, openAuth, categoryIdByName, navigate],
  );

  // All the interactive scripts — ported from the original inline <script> blocks.
  // Kept as a single effect so teardown order matches setup order.
  useEffect(() => {
    const cleanups: Array<() => void> = [];

    // ── Background columns ────────────────────────────────────────────
    {
      const COLS = 15, GAP = 0, PROXIMITY = 280;
      const cols: Array<[number, number, number, number] & { _f?: number }> = [
        [208,212,238,0.08],[207,211,238,0.16],[206,211,237,0.24],[205,210,237,0.30],
        [205,210,237,0.32],[208,212,238,0.29],[213,213,239,0.27],[219,215,239,0.26],
        [226,217,240,0.28],[233,219,241,0.30],[238,221,241,0.30],[236,220,241,0.27],
        [228,217,239,0.21],[220,214,238,0.14],[214,213,237,0.08],
      ] as unknown as Array<[number, number, number, number] & { _f?: number }>;
      const container = document.getElementById("bgColumns");
      if (!container) return;
      let rects: Array<{ el: SVGRectElement; cx: number; r: number; g: number; b: number; a: number }> = [];
      let svgEl: SVGSVGElement | null = null;

      function build() {
        if (!container) return;
        const W = container.offsetWidth, H = container.offsetHeight;
        container.innerHTML = "";
        rects = [];
        const ns = "http://www.w3.org/2000/svg";
        svgEl = document.createElementNS(ns, "svg");
        svgEl.setAttribute("viewBox", `0 0 ${W} ${H}`);
        svgEl.setAttribute("preserveAspectRatio", "none");
        svgEl.style.cssText = "width:100%;height:100%";
        const defs = document.createElementNS(ns, "defs");
        const BG = "#fafafa", colW = (W - GAP * (COLS - 1)) / COLS, extraH = 100, center = (COLS - 1) / 2;
        cols.forEach((c, i) => {
          const dist = Math.abs(i - center) / center, fadeRatio = 0.04 + dist * 0.44;
          (cols[i] as [number, number, number, number] & { _f?: number })._f = fadeRatio;
          (["t", "b"] as const).forEach(dir => {
            const grad = document.createElementNS(ns, "linearGradient");
            grad.setAttribute("id", `f${dir}${i}`);
            grad.setAttribute("x1", "0"); grad.setAttribute("y1", "0");
            grad.setAttribute("x2", "0"); grad.setAttribute("y2", "1");
            const stops: Array<[string, number]> = dir === "t" ? [["0%", 1], ["100%", 0]] : [["0%", 0], ["100%", 1]];
            stops.forEach(([off, op]) => {
              const s = document.createElementNS(ns, "stop");
              s.setAttribute("offset", off); s.setAttribute("stop-color", BG); s.setAttribute("stop-opacity", String(op));
              grad.appendChild(s);
            });
            defs.appendChild(grad);
          });
          void c;
        });
        svgEl.appendChild(defs);
        cols.forEach((col, i) => {
          const [r, g, b, a] = col;
          const x = i * (colW + GAP);
          const rect = document.createElementNS(ns, "rect");
          rect.setAttribute("x", String(x)); rect.setAttribute("y", String(-(extraH / 2)));
          rect.setAttribute("width", String(colW)); rect.setAttribute("height", String(H + extraH));
          rect.setAttribute("rx", "2"); rect.setAttribute("fill", `rgba(${r},${g},${b},${a})`);
          rect.classList.add("col-rect");
          rect.style.animationDelay = `${-(i * 0.9).toFixed(1)}s`;
          svgEl!.appendChild(rect);
          rects.push({ el: rect, cx: x + colW / 2, r, g, b, a });
        });
        cols.forEach((col, i) => {
          const x = i * (colW + GAP);
          const fadeH = H * (col as [number, number, number, number] & { _f?: number })._f!;
          (["t", "b"] as const).forEach(dir => {
            const ov = document.createElementNS(ns, "rect");
            ov.setAttribute("x", String(x)); ov.setAttribute("y", String(dir === "t" ? 0 : H - fadeH));
            ov.setAttribute("width", String(colW)); ov.setAttribute("height", String(fadeH));
            ov.setAttribute("fill", `url(#f${dir}${i})`);
            ov.style.pointerEvents = "none";
            svgEl!.appendChild(ov);
          });
        });
        container.appendChild(svgEl);
      }

      const onMove = (e: MouseEvent) => {
        if (!svgEl) return;
        const b = svgEl.getBoundingClientRect(), mx = e.clientX - b.left;
        rects.forEach(({ el, cx, r, g, b: bv, a }) => {
          const str = Math.max(0, 1 - Math.abs(cx - mx) / PROXIMITY);
          if (str > 0.01) {
            const na = Math.min(a + str * 0.35, 0.75);
            const cr = Math.round(r + (180 - r) * str * 0.25);
            const cg = Math.round(bv + (190 - bv) * str * 0.20);
            const cb = Math.round(bv + (240 - bv) * str * 0.30);
            el.setAttribute("fill", `rgba(${cr},${cg},${cb},${na})`);
            if (str > 0.7) { el.style.animationPlayState = "paused"; el.style.filter = `drop-shadow(0 0 20px rgba(180,190,240,${str * 0.25}))`; }
            else { el.style.animationPlayState = ""; el.style.filter = ""; }
          } else {
            el.setAttribute("fill", `rgba(${r},${g},${bv},${a})`);
            el.style.animationPlayState = ""; el.style.filter = "";
          }
        });
      };
      const onLeave = () => {
        rects.forEach(({ el, r, g, b: bv, a }) => {
          el.setAttribute("fill", `rgba(${r},${g},${bv},${a})`);
          el.style.animationPlayState = ""; el.style.filter = "";
        });
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseleave", onLeave);
      window.addEventListener("resize", build);
      build();
      cleanups.push(() => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseleave", onLeave);
        window.removeEventListener("resize", build);
      });
    }

    // ── Gold sine-wave animation ──────────────────────────────────────
    {
      const svg = document.getElementById("goldArches") as unknown as SVGSVGElement | null;
      if (!svg) return;
      const NS = "http://www.w3.org/2000/svg";
      type Wave = { yOff: number; restAmp: number; activeAmp: number; freq: number; speed: number; phase: number; sw: number; col: string; restOp: number; activeOp: number };
      const WAVES: Wave[] = [
        { yOff: -6,   restAmp:14, activeAmp:22, freq:1.40, speed:0.000800, phase:0.00, sw:1.10, col:"#c8c428", restOp:0.40, activeOp:0.58 },
        { yOff: 10,   restAmp:12, activeAmp:20, freq:1.75, speed:0.000620, phase:2.10, sw:0.90, col:"#d4c030", restOp:0.34, activeOp:0.52 },
        { yOff: -52,  restAmp:28, activeAmp:40, freq:1.10, speed:0.000500, phase:1.50, sw:1.00, col:"#c0b828", restOp:0.26, activeOp:0.40 },
        { yOff: -108, restAmp:40, activeAmp:52, freq:0.85, speed:0.000370, phase:3.70, sw:0.85, col:"#d4c840", restOp:0.20, activeOp:0.32 },
        { yOff: -170, restAmp:55, activeAmp:66, freq:0.65, speed:0.000270, phase:5.20, sw:0.75, col:"#c8c428", restOp:0.15, activeOp:0.25 },
        { yOff:  60,  restAmp:28, activeAmp:40, freq:1.20, speed:0.000530, phase:0.80, sw:1.00, col:"#c8c030", restOp:0.26, activeOp:0.40 },
        { yOff: 115,  restAmp:42, activeAmp:52, freq:0.90, speed:0.000320, phase:2.50, sw:0.85, col:"#d4c840", restOp:0.20, activeOp:0.32 },
        { yOff: 175,  restAmp:58, activeAmp:68, freq:0.70, speed:0.000240, phase:4.10, sw:0.75, col:"#c8c428", restOp:0.15, activeOp:0.24 },
      ];

      let paths: SVGPathElement[] = [];
      let typing = false;
      let typingTimer: ReturnType<typeof setTimeout> | null = null;
      const curOp = WAVES.map(w => w.restOp);
      const curAmp = WAVES.map(w => w.restAmp);
      const curPhase = WAVES.map(w => w.phase);
      let lastTs: number | null = null;
      let rafId = 0;

      function sineWavePath(W: number, baseY: number, amp: number, freq: number, phase: number) {
        const steps = 320;
        const pts: string[] = [];
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          const x = t * W;
          const y = baseY + amp * Math.sin(freq * t * Math.PI * 2 + phase);
          pts.push(`${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`);
        }
        return pts.join(" ");
      }

      function getBarCY() {
        const bar = document.getElementById("searchBar");
        if (!bar) return null;
        const r = bar.getBoundingClientRect();
        return r.top + r.height / 2 + (window.scrollY || 0);
      }

      function setup() {
        if (!svg) return;
        while (svg.firstChild) svg.removeChild(svg.firstChild);
        paths = [];
        const W = window.innerWidth;
        const pageEl = document.querySelector(".landing-page .page") as HTMLElement | null;
        const H = Math.max(pageEl?.scrollHeight ?? 0, window.innerHeight);
        svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
        svg.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible";

        const defs = document.createElementNS(NS, "defs");
        const filt = document.createElementNS(NS, "filter");
        filt.setAttribute("id", "waveGlow");
        filt.setAttribute("x", "-10%"); filt.setAttribute("y", "-300%");
        filt.setAttribute("width", "120%"); filt.setAttribute("height", "700%");
        const blur = document.createElementNS(NS, "feGaussianBlur");
        blur.setAttribute("stdDeviation", "2.5"); blur.setAttribute("result", "b");
        const merge = document.createElementNS(NS, "feMerge");
        ["b", "SourceGraphic"].forEach(n => {
          const mn = document.createElementNS(NS, "feMergeNode");
          mn.setAttribute("in", n); merge.appendChild(mn);
        });
        filt.appendChild(blur); filt.appendChild(merge);
        defs.appendChild(filt);

        WAVES.forEach((w, i) => {
          const grad = document.createElementNS(NS, "linearGradient");
          grad.setAttribute("id", `wg${i}`);
          grad.setAttribute("x1", "0%"); grad.setAttribute("y1", "0%");
          grad.setAttribute("x2", "100%"); grad.setAttribute("y2", "0%");
          ([["0%", 0], ["10%", 1], ["90%", 1], ["100%", 0]] as const).forEach(([off, m]) => {
            const s = document.createElementNS(NS, "stop");
            s.setAttribute("offset", off);
            s.setAttribute("stop-color", w.col);
            s.setAttribute("stop-opacity", String(m));
            grad.appendChild(s);
          });
          defs.appendChild(grad);
        });
        svg.appendChild(defs);

        WAVES.forEach((w, i) => {
          const path = document.createElementNS(NS, "path");
          path.setAttribute("fill", "none");
          path.setAttribute("stroke", `url(#wg${i})`);
          path.setAttribute("stroke-width", String(w.sw));
          path.setAttribute("stroke-linecap", "round");
          path.setAttribute("stroke-linejoin", "round");
          path.setAttribute("opacity", String(curOp[i]));
          svg.appendChild(path);
          paths.push(path);
        });
      }

      function animate(ts: number) {
        const barCY = getBarCY();
        if (barCY === null) { rafId = requestAnimationFrame(animate); return; }
        const dt = lastTs === null ? 0 : ts - lastTs;
        lastTs = ts;
        const W = window.innerWidth;
        WAVES.forEach((w, i) => {
          const tOp = typing ? w.activeOp : w.restOp;
          const tAmp = typing ? w.activeAmp : w.restAmp;
          curOp[i] += (tOp - curOp[i]) * 0.055;
          curAmp[i] += (tAmp - curAmp[i]) * 0.040;
          if (typing) curPhase[i] += w.speed * dt;
          paths[i].setAttribute("opacity", curOp[i].toFixed(3));
          const baseY = barCY + w.yOff;
          paths[i].setAttribute("d", sineWavePath(W, baseY, curAmp[i], w.freq, curPhase[i]));
          paths[i].setAttribute("filter", typing ? "url(#waveGlow)" : "");
        });
        rafId = requestAnimationFrame(animate);
      }

      function startTyping() {
        typing = true;
        document.getElementById("searchBar")?.classList.add("typing-active");
        if (typingTimer) clearTimeout(typingTimer);
      }
      function stopTyping() {
        typingTimer = setTimeout(() => {
          typing = false;
          document.getElementById("searchBar")?.classList.remove("typing-active");
        }, 1600);
      }

      const input = document.getElementById("searchInput") as HTMLInputElement | null;
      const onInput = () => { startTyping(); stopTyping(); };
      const onKey = () => startTyping();
      const onBlur = () => stopTyping();
      input?.addEventListener("input", onInput);
      input?.addEventListener("keydown", onKey);
      input?.addEventListener("blur", onBlur);

      window.addEventListener("resize", setup);
      setup();
      rafId = requestAnimationFrame(animate);

      cleanups.push(() => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", setup);
        input?.removeEventListener("input", onInput);
        input?.removeEventListener("keydown", onKey);
        input?.removeEventListener("blur", onBlur);
        if (typingTimer) clearTimeout(typingTimer);
      });
    }

    // ── UI interactions (menu, thinking, typewriter) ──────────────────
    {
      const hamburgerBtn = document.getElementById("hamburgerBtn");
      const mobileMenu = document.getElementById("mobileMenu");
      const toggleMenu = () => {
        hamburgerBtn?.classList.toggle("open");
        mobileMenu?.classList.toggle("open");
      };
      const onDocClick = (e: MouseEvent) => {
        if (!hamburgerBtn || !mobileMenu) return;
        if (!hamburgerBtn.contains(e.target as Node) && !mobileMenu.contains(e.target as Node)) {
          hamburgerBtn.classList.remove("open");
          mobileMenu.classList.remove("open");
        }
      };
      hamburgerBtn?.addEventListener("click", toggleMenu);
      document.addEventListener("click", onDocClick);

      const CARDS = [
        { label:"Quantity MOQ",   sub:"BULK PRICING ACTIVE",    dot:"#a78bfa", x:18,  y:28 },
        { label:"SKU Analysis",   sub:"847 PRODUCTS MATCHED",   dot:"#4ade80", x:55,  y:18 },
        { label:"Stock Status",   sub:"AVAILABILITY SYNC",      dot:"#4ade80", x:10,  y:44 },
        { label:"Price Range",    sub:"QAR 650–800",            dot:"#fb923c", x:72,  y:30 },
        { label:"Alternatives",   sub:"SCANNING OPTIONS",       dot:"#a78bfa", x:80,  y:65 },
        { label:"Compliance",     sub:"STANDARDS CHECK",        dot:"#c084fc", x:22,  y:62 },
        { label:"Supplier Rating",sub:"VERIFIED SOURCES",       dot:"#60a5fa", x:76,  y:55 },
        { label:"Lead Time",      sub:"2–3 WEEK WINDOW",        dot:"#4ade80", x:14,  y:72 },
        { label:"Delivery Zone",  sub:"DOHA SUPPLIERS",         dot:"#f472b6", x:68,  y:72 },
      ];
      const STARS = [
        { col:"#cbd5e1", sz:"32px", x:4,   y:8,  dur:3.6, del:0.0 },
        { col:"#00D2E8", sz:"20px", x:88,  y:8,  dur:4.1, del:0.6 },
        { col:"#94a3b8", sz:"26px", x:38,  y:8,  dur:3.2, del:1.3 },
        { col:"#64748b", sz:"18px", x:62,  y:8,  dur:4.4, del:0.9 },
        { col:"#00D2E8", sz:"36px", x:4,   y:35, dur:3.9, del:1.9 },
        { col:"#cbd5e1", sz:"16px", x:90,  y:38, dur:3.3, del:2.3 },
        { col:"#94a3b8", sz:"22px", x:38,  y:38, dur:4.3, del:0.4 },
        { col:"#60a5fa", sz:"18px", x:48,  y:52, dur:3.7, del:1.6 },
        { col:"#cbd5e1", sz:"28px", x:88,  y:28, dur:4.0, del:2.9 },
        { col:"#00D2E8", sz:"20px", x:40,  y:55, dur:3.5, del:0.7 },
        { col:"#64748b", sz:"14px", x:4,   y:82, dur:4.6, del:3.2 },
        { col:"#94a3b8", sz:"24px", x:40,  y:80, dur:3.1, del:1.1 },
        { col:"#cbd5e1", sz:"16px", x:90,  y:80, dur:4.2, del:2.1 },
        { col:"#00D2E8", sz:"22px", x:60,  y:42, dur:3.8, del:0.3 },
      ];

      let thinkingResultTimer: ReturnType<typeof setTimeout> | null = null;
      let pendingQuery = "";

      // Shared by both submission paths (first turn and follow-up) — the Ask
      // button is disabled for a blank/whitespace query, but Enter still
      // reaches this via the input's keydown listener.
      function rejectEmptyQuery(inputEl: HTMLInputElement) {
        showErrorToast("Please describe what you need before submitting.");
        const bar = document.getElementById("searchBar");
        if (bar) {
          bar.style.animation = "none";
          void bar.offsetHeight;
          bar.style.animation = "lp-shake 0.4s ease";
        }
        inputEl.focus();
      }

      function showResults() {
        document.getElementById("thinkingOverlay")?.classList.add("results-mode");
        const btn = document.getElementById("askBtn") as HTMLButtonElement | null;
        if (btn) {
          btn.innerHTML = "Ask Procurtal";
          btn.classList.remove("stop-mode");
          btn.onclick = handleAsk;
          // Follow-up field is cleared below, so the button goes back to disabled
          // until the user types the next query.
          btn.disabled = true;
        }
        // Re-enable the hero search so the user can type a follow-up.
        const inp = document.getElementById("searchInput") as HTMLInputElement | null;
        if (inp) { inp.disabled = false; inp.value = ""; inp.placeholder = "Ask a follow-up..."; }
        setResultsOpenRef.current(true);
        onResultsOpenChangeRef.current?.(true);
        // Fire the caller's handler as a side effect so the chat feature can
        // persist/stream this turn — the landing UI itself is unchanged.
        if (pendingQuery) onSearchRef.current?.(pendingQuery);
      }

      function buildThinkingOverlay() {
        const overlay = document.getElementById("thinkingOverlay");
        if (!overlay) return;
        overlay.querySelectorAll(".t-star,.t-card").forEach(el => el.remove());
        const bar = document.getElementById("searchBar");
        if (!bar) return;
        const barRect = bar.getBoundingClientRect();
        const vh = window.innerHeight;
        const maxStarRadiusPx = 18; // Largest star is 36px wide.
        const starClearancePx = 20;
        const maxStarY = ((barRect.top - maxStarRadiusPx - starClearancePx) / vh) * 100;
        const maxCardY = ((barRect.top - 12) / vh) * 100;
        const clampStarY = (y: number) => Math.min(y, Math.max(8, maxStarY));
        const clampCardY = (y: number) => Math.min(y, Math.max(16, maxCardY));

        STARS.forEach(s => {
          const el = document.createElement("div");
          el.className = "t-star";
          el.style.cssText = `left:${s.x}%;top:${clampStarY(s.y)}%;--col:${s.col};--sz:${s.sz};--dur:${s.dur}s;--del:${s.del}s;`;
          el.innerHTML = `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><path d="M20,0 L21.5,18.5 L40,20 L21.5,21.5 L20,40 L18.5,21.5 L0,20 L18.5,18.5Z"/></svg>`;
          const glow = document.createElement("div");
          glow.className = "t-star-glow";
          el.appendChild(glow);
          overlay.appendChild(el);
        });

        const cardDur = 9.0;
        const groupGap = cardDur / 3;
        CARDS.forEach((c, i) => {
          const el = document.createElement("div");
          el.className = "t-card";
          const group = Math.floor(i / 3);
          const pos = i % 3;
          const del = group * groupGap + pos * 0.35;
          el.style.cssText = `left:${c.x}%;top:${clampCardY(c.y)}%;--dot:${c.dot};--del:${del.toFixed(2)}s;--dur:${cardDur}s;`;
          el.innerHTML = `<div class="t-card-row"><div class="t-card-dot"></div><span class="t-card-label">${c.label}</span></div><div class="t-card-sub">${c.sub}</div>`;
          overlay.appendChild(el);
        });
      }

      function startThinking() {
        const input = document.getElementById("searchInput") as HTMLInputElement | null;
        if (!input) return;
        const val = input.value.trim();
        if (!val) {
          rejectEmptyQuery(input);
          return;
        }

        pendingQuery = val;
        input.value = "";

        const pill = document.getElementById("thinkingQuery");
        if (pill) pill.textContent = val;
        buildThinkingOverlay();

        // Reset scroll positions before the thinking animation runs. Body /
        // main-container locking is owned by the chatDocked useEffect.
        document.getElementById("main-container")?.scrollTo({ top: 0, behavior: "auto" });
        window.scrollTo({ top: 0, behavior: "auto" });
        const overlay = document.getElementById("thinkingOverlay");
        overlay?.removeAttribute("aria-hidden");
        // Follow-up turns come back through here — clear `results-mode` so
        // stars + cards animate again instead of rendering over the old panel.
        overlay?.classList.remove("results-mode");
        overlay?.classList.add("active");
        setResultsOpenRef.current(false);
        onResultsOpenChangeRef.current?.(false);

        const btn = document.getElementById("askBtn") as HTMLButtonElement | null;
        if (btn) {
          btn.innerHTML = '<div class="stop-sq"></div>Stop';
          btn.classList.add("stop-mode");
          btn.onclick = stopThinking;
        }

        input.disabled = true;
        document.getElementById("heroText")?.classList.add("thinking-hide");
        setChatDockedRef.current(true);
        document.getElementById("searchHint")?.classList.add("thinking-hide");
        document.getElementById("heroFeatures")?.classList.add("thinking-hide");

        thinkingResultTimer = setTimeout(showResults, 2000);
      }

      function stopThinking() {
        const overlay = document.getElementById("thinkingOverlay");
        overlay?.classList.remove("active");
        overlay?.setAttribute("aria-hidden", "true");
        // Body / main-container scroll lock is released by the chatDocked
        // useEffect cleanup once setChatDockedRef.current(false) fires below.

        const btn = document.getElementById("askBtn") as HTMLButtonElement | null;
        if (btn) {
          btn.innerHTML = "Ask Procurtal";
          btn.classList.remove("stop-mode");
          btn.onclick = handleAsk;
          // startThinking() already cleared the input, so go back to disabled.
          btn.disabled = true;
        }

        const input = document.getElementById("searchInput") as HTMLInputElement | null;
        if (input) { input.disabled = false; input.focus(); }
        document.getElementById("heroText")?.classList.remove("thinking-hide");
        setChatDockedRef.current(false);
        document.getElementById("searchHint")?.classList.remove("thinking-hide");
        document.getElementById("heroFeatures")?.classList.remove("thinking-hide");

        if (thinkingResultTimer) clearTimeout(thinkingResultTimer);
        setResultsOpenRef.current(false);
        overlay?.classList.remove("results-mode");
        onResultsOpenChangeRef.current?.(false);
      }

      // Submits whatever is typed — used by Enter, which should always try to
      // send/queue the next message rather than stop the current one. Also
      // used by the Ask button itself once responding/retrying isn't in play.
      function submitQuery() {
        // Once the recommendations panel is up, each new submission is a
        // chat follow-up — skip the thinking animation and push the query
        // straight at the chat handler.
        if (resultsOpenRef.current) {
          const input = document.getElementById("searchInput") as HTMLInputElement | null;
          if (!input) return;
          const val = input.value.trim();
          // A typed message always wins over Retry. Checking Retry first
          // re-sent the last failed message on every Enter and left the new
          // one sitting in the input.
          if (!val && chatRetryRef.current) {
            chatRetryRef.current();
            return;
          }
          if (!val) {
            rejectEmptyQuery(input);
            return;
          }
          input.value = "";
          // While a response is still streaming, this is queued and sent
          // automatically once it finishes (see useChat.sendMessage) — the
          // input stays usable instead of blocking until the turn settles.
          onSearchRef.current?.(val);
          return;
        }
        startThinking();
      }

      // Button click: while a response is streaming, Stop takes priority —
      // the input keeps accepting/sending the next message independently via
      // Enter (submitQuery), so nothing is lost by the button not sending too.
      function handleAsk() {
        if (resultsOpenRef.current && chatRespondingRef.current) {
          chatStopRef.current?.();
          return;
        }
        // The button reads "Retry" after a failed reply — do what it says,
        // even with text typed (Enter sends that text instead).
        if (resultsOpenRef.current && chatRetryRef.current) {
          chatRetryRef.current();
          return;
        }
        submitQuery();
      }

      const askBtn = document.getElementById("askBtn") as HTMLButtonElement | null;
      if (askBtn) askBtn.onclick = handleAsk;

      const input = document.getElementById("searchInput") as HTMLInputElement | null;
      // Disabled by default (field starts empty) — only "Ask" mode is gated on
      // input text; Stop/Retry stay clickable regardless, so they're excluded.
      if (askBtn) askBtn.disabled = !input?.value.trim();
      const onValueInput = () => {
        if (!askBtn || !input) return;
        if (askBtn.classList.contains("stop-mode") || askBtn.classList.contains("retry-mode")) return;
        askBtn.disabled = !input.value.trim();
      };
      input?.addEventListener("input", onValueInput);
      const onKeydown = (e: KeyboardEvent) => { if (e.key === "Enter") submitQuery(); };
      input?.addEventListener("keydown", onKeydown);

      // Run a handed-over query through the same path as a typed one, so it
      // gets the thinking animation and the results panel. Under StrictMode's
      // double-invoked effects the second run simply restarts it — cleanup
      // already cancelled the first run's pending results timer.
      const initialQueryValue = initialQueryRef.current?.trim();
      if (input && initialQueryValue) {
        input.value = initialQueryValue;
        submitQuery();
      }

      // Typewriter placeholder
      const suggestions = [
        "20 laptops for a new engineering team, budget 3,500 QAR each",
        "50 standing desks and ergonomic chairs for our Doha office",
        "Annual stationery and office supplies contract for 200 staff",
        "Industrial HVAC maintenance for a 5,000 sqm warehouse",
        "Catering and AV equipment for a 3-day corporate conference",
        "Fleet of 10 electric vehicles for executive transport",
        "IT security software licenses for 300 employees",
        "Uniforms and PPE for a 120-person construction crew",
      ];
      let sIdx = 0, charIdx = 0;
      const timers: Array<ReturnType<typeof setTimeout>> = [];
      function typeSuggestion() {
        const inp = document.getElementById("searchInput") as HTMLInputElement | null;
        if (!inp) return;
        // Once the user has submitted a query and the results panel is up,
        // stop cycling example placeholders — the search bar is now in
        // follow-up mode and the rotating hint becomes noise.
        if (resultsOpenRef.current) return;
        if (document.activeElement === inp) return;
        const text = suggestions[sIdx];
        if (charIdx < text.length) {
          inp.placeholder = text.slice(0, ++charIdx);
          timers.push(setTimeout(typeSuggestion, 45));
        } else {
          timers.push(setTimeout(() => {
            (function erase() {
              if (charIdx > 0) {
                inp.placeholder = text.slice(0, --charIdx);
                timers.push(setTimeout(erase, 25));
              } else {
                sIdx = (sIdx + 1) % suggestions.length;
                timers.push(setTimeout(typeSuggestion, 600));
              }
            })();
          }, 2200));
        }
      }
      timers.push(setTimeout(typeSuggestion, 1000));
      const onFocus = function (this: HTMLInputElement) {
        if (resultsOpenRef.current) return;
        this.placeholder = "";
      };
      const onBlur = function () {
        if (resultsOpenRef.current) return;
        charIdx = 0;
        timers.push(setTimeout(typeSuggestion, 600));
      };
      input?.addEventListener("focus", onFocus);
      input?.addEventListener("blur", onBlur);

      cleanups.push(() => {
        hamburgerBtn?.removeEventListener("click", toggleMenu);
        document.removeEventListener("click", onDocClick);
        input?.removeEventListener("input", onValueInput);
        input?.removeEventListener("keydown", onKeydown);
        input?.removeEventListener("focus", onFocus);
        input?.removeEventListener("blur", onBlur);
        if (thinkingResultTimer) clearTimeout(thinkingResultTimer);
        timers.forEach(t => clearTimeout(t));
        document.body.style.overflow = "";
      });
    }

    return () => { cleanups.forEach(c => c()); };
  }, []);

  useEffect(() => {
    if (!resultsOpen) return;
    const btn = document.getElementById("askBtn") as HTMLButtonElement | null;
    const input = document.getElementById("searchInput") as HTMLInputElement | null;
    if (!btn) return;
    if (chatResponding) {
      btn.innerHTML = '<div class="stop-sq"></div>Stop';
      btn.classList.add("stop-mode");
      btn.classList.remove("retry-mode");
      // showResults() disables the button right before firing the first turn,
      // and onValueInput early-returns while stop-mode is on, so without this
      // the very first Stop is rendered but permanently unclickable.
      btn.disabled = false;
      // Input stays enabled — the next message can be typed and sent (Enter)
      // while this one streams; it's queued and sent automatically once this
      // turn finishes (see useChat.sendMessage). The button itself keeps
      // meaning "Stop" while responding, regardless of what's typed.
    } else if (chatRetry) {
      btn.innerHTML = "Retry";
      btn.classList.remove("stop-mode");
      btn.classList.add("retry-mode");
      // Retry must be clickable even when the input is empty.
      btn.disabled = false;
      if (input) { input.disabled = false; input.focus(); }
    } else {
      btn.innerHTML = "Ask Procurtal";
      btn.classList.remove("stop-mode");
      btn.classList.remove("retry-mode");
      if (input) { input.disabled = false; }
      btn.disabled = !input?.value.trim();
    }
  }, [chatResponding, chatRetry, resultsOpen]);

  const goSignIn = () => navigate(AppRoutes.LOGIN);
  const goSignUp = () => navigate(AppRoutes.CREATE_ACCOUNT);

  return (
    <div className="landing-page">
      <div className="page">
        <div className="bg-columns" aria-hidden="true" id="bgColumns"></div>
        <svg id="goldArches" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"></svg>

        <div id="thinkingOverlay" className="thinking-overlay" aria-hidden="true">
          <div className="thinking-bg"></div>
          <div className="thinking-query-pill" id="thinkingQuery"></div>
          <div className="thinking-status">ANALYZING REQUIREMENTS</div>
          <AiChatPanel
            visible={resultsOpen}
            loggedIn={embedded}
            transcript={chatTranscript ?? []}
            responding={chatResponding ?? false}
            onAuthRequired={() => openAuth("login")}
            onRetry={chatRetry}
            onSendMessage={chatSendMessage}
          />
        </div>

        {!embedded && (
          <nav className={chatDocked ? "nav-opaque" : undefined}>
            <a className="logo" href="/">
              <img className="logo-svg" src="/logo.svg" alt="Procurtal" />
            </a>
            <div className="nav-right">
              <button className="btn-signin" onClick={goSignIn}>Sign In</button>
              {/* TODO: hamburger menu hidden until mobile nav is designed
              <button className="hamburger" id="hamburgerBtn" aria-label="Toggle menu">
                <span></span><span></span><span></span>
              </button>
              */}
            </div>
            {/* TODO: mobile menu hidden until nav links are finalized
            <div className="mobile-menu" id="mobileMenu">
              <a href="#">Start Selling</a>
              <a href="#">How it Works</a>
              <a href="#">Features</a>
            </div>
            */}
          </nav>
        )}

        <section className={`hero${chatDocked ? " hero-docked" : ""}`}>
          <div className="hero-inner">
            <div id="heroText">
              <div className="eyebrow">
                <div className="eyebrow-line"></div>
                <span className="eyebrow-text">Procurement, Re-imagined</span>
              </div>
              <h1 className="hero-heading">Your Shortcut to<br />Intelligent Sourcing</h1>
              <p className="hero-sub">
                Tell us what your organisation needs in plain language — Procurtal finds options, compares them, and prepares your quote instantly.
              </p>
            </div>

            <div className={`search-wrap${chatDocked ? " thinking-lower" : ""}`} id="searchWrap">
              <div
                className="search-bar"
                id="searchBar"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("#askBtn")) return;
                  document.getElementById("searchInput")?.focus();
                }}
              >
                <div className="search-inner">
                  <svg className="search-icon" viewBox="0 0 20 20" fill="none">
                    <circle cx="8.5" cy="8.5" r="5.75" stroke="#6a7282" strokeWidth="1.5" />
                    <path d="M13 13L17 17" stroke="#6a7282" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <input
                    className="search-input"
                    id="searchInput"
                    type="text"
                    placeholder="Describe what you need..."
                    aria-label="Describe your procurement need"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </div>
                <Button id="askBtn" className="btn-ask">Ask Procurtal</Button>
              </div>
              <p className="search-hint" id="searchHint">e.g., "I need 20 laptops for office use under 3,500 QAR"</p>
            </div>

            <div className="features" id="heroFeatures">
              {[
                { icon: featureIcons.rewards, label: "Built-in Rewards" },
                { icon: featureIcons.quotation, label: "Fast Quotation" },
                { icon: featureIcons.intelligence, label: "Procurement Intelligence" },
              ].map(({ icon, label }) => (
                <div className="feature-pill" key={label}>
                  <div className="feature-icon-wrap">
                    <img src={icon} alt="" className="feature-icon-img" />
                  </div>
                  <span className="feature-label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {!chatDocked && (
        <>
        {afterHero}

        <section className="why-section" data-reveal>
          <div className="why-header">
            <h2 className="why-title">Why Procurement Teams Choose Procurtal</h2>
            <p className="why-subtitle">Built for the way modern businesses buy</p>
          </div>
          <div className="why-cards" data-reveal data-reveal-trigger="stagger">

            <div className="why-card why-card--smart-rewards">
              <div className="why-card-bg why-card-bg--smart-rewards">
                {smartRewardsWaves.map((wave, i) => (
                  <img key={wave} src={wave} alt="" className={`why1-wave why1-wave--${i + 1}`} />
                ))}
              </div>
              <div className="why-card-content">
                <div className="why-icon-badge why-icon-badge--smart-rewards">
                  <img src={whyBadges.smartRewards} alt="" />
                </div>
                <div className="why-card-text">
                  <h3 className="why-card-title">Smart Rewards</h3>
                  <p className="why-card-body">Earn points on every order and turn procurement activity into long-term purchasing value.</p>
                </div>
              </div>
            </div>

            <div className="why-card">
              <div className="why-card-bg" dangerouslySetInnerHTML={{ __html: INTELLIGENT_CARD_SVG }} />
              <div className="why-card-content">
                <div className="why-icon-badge why-icon-badge--intelligent">
                  <img src={whyBadges.intelligentProcurement} alt="" />
                </div>
                <div className="why-card-text">
                  <h3 className="why-card-title">Intelligent Procurement</h3>
                  <p className="why-card-body">Compare products clearly through structured insights covering value, warranty, and best-fit options.</p>
                </div>
              </div>
            </div>

            <div className="why-card">
              <div className="why-card-bg why-card-bg--photo">
                <img src={whyBackgrounds.instantQuotations} alt="" className="why-card-bg-img" />
              </div>
              <div className="why-card-content">
                <div className="why-icon-badge why-icon-badge--quotations">
                  <img src={whyBadges.instantQuotations} alt="" />
                </div>
                <div className="why-card-text">
                  <h3 className="why-card-title">Instant Quotations</h3>
                  <p className="why-card-body">Receive quotations quickly and move from request to decision with less delay.</p>
                </div>
              </div>
            </div>

            <div className="why-card">
              <div className="why-card-bg why-card-bg--photo">
                <img src={whyBackgrounds.flexiblePayments} alt="" className="why-card-bg-img" />
              </div>
              <div className="why-card-content">
                <div className="why-icon-badge why-icon-badge--payments">
                  <img src={whyBadges.flexiblePayments} alt="" />
                </div>
                <div className="why-card-text">
                  <h3 className="why-card-title">Flexible Payments</h3>
                  <p className="why-card-body">Selected products can be purchased through installment options using supported payment methods.</p>
                </div>
              </div>
            </div>

          </div>
        </section>

        <section className="steps-section" data-reveal>
          <div className="steps-waves">
            {stepsWaves.map((wave, i) => {
              const flipped = i === 0 || i === 3 || i === 4;
              return (
                <div key={wave} className={`sw-w sw-w${i + 1}`}>
                  <img src={wave} alt="" className={flipped ? "sw-w-flipped" : undefined} />
                </div>
              );
            })}
          </div>
          <div className="steps-header">
            <h2 className="steps-title">Procurement in Three Simple Steps</h2>
            <p className="steps-subtitle">From request to delivery, every step is designed to reduce friction.</p>
          </div>
          <div className="steps-row" data-reveal data-reveal-trigger="stagger">
            <div className="step-col">
              <span className="step-num">1.</span>
              <h3 className="step-heading">Describe Your Need</h3>
              <p className="step-body">Use AI-powered search to define exactly what your business requires.</p>
            </div>
            <div className="step-connector"></div>
            <div className="step-col">
              <span className="step-num">2.</span>
              <h3 className="step-heading">Compare and Select</h3>
              <p className="step-body">Review structured options with pricing, warranty, and fit clearly presented.</p>
            </div>
            <div className="step-connector"></div>
            <div className="step-col">
              <span className="step-num">3.</span>
              <h3 className="step-heading">Order with Confidence</h3>
              <p className="step-body">Place orders securely with clear fulfilment and payment options.</p>
            </div>
          </div>
        </section>

        <section className="cat-section" data-reveal>
          <div className="cat-header">
            <h2 className="cat-title">Shop by category</h2>
            <p className="cat-subtitle">Explore our most requested categories</p>
            <div className="cat-pill">Browse our catalog</div>
          </div>
          <div className="cat-grid" data-reveal data-reveal-trigger="stagger">
            {[
              { name: "Laptops", img: categoryImages.laptops },
              { name: "Desktops", img: categoryImages.desktops },
              { name: "Printers", img: categoryImages.printers },
              { name: "Smartphones", img: categoryImages.smartphones },
            ].map(cat => (
              <div
                className="cat-card"
                key={cat.name}
                role="button"
                tabIndex={0}
                onClick={() => handleCategoryClick(cat.name)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCategoryClick(cat.name);
                  }
                }}
              >
                <div className="cat-card-img"><img src={cat.img} alt={cat.name} /></div>
                <div className="cat-card-body">
                  <h3 className="cat-card-name">{cat.name}</h3>
                  <a className="cat-explore">Explore {ARROW_SVG}</a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {!embedded && (
        <>
        <section className="cta-section" data-reveal>
          {ctaWaves.map((wave, i) => (
            <img key={wave} className={`cta-wave cta-wave--${i + 1}`} src={wave} alt="" />
          ))}
          <div className="cta-content">
            <h2 className="cta-title">Procurement, Reimagined for<br />Modern Business</h2>
            <p className="cta-subtitle">A smarter procurement experience designed for speed, clarity, and control.</p>
            <div className="cta-btns">
              <button className="cta-btn-primary" onClick={goSignUp}>Start Sourcing</button>
              <button className="cta-btn-secondary" onClick={goSignUp}>Become a Supplier</button>
            </div>
          </div>
        </section>

        <footer className="site-footer">
          <div className="footer-main">
            <div className="footer-brand">
              <img src="/logo.svg" alt="Procurtal" style={{ height: 40 }} />
              <p className="footer-tagline">AI-powered procurement for modern businesses.</p>
            </div>
            <div className="footer-col">
              <h4 className="footer-col-title">Legal</h4>
              <ul className="footer-col-list">
                <li><Link to={AppRoutes.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer">Privacy Policy</Link></li>
                <li><Link to={AppRoutes.TERMS_AND_CONDITIONS} target="_blank" rel="noopener noreferrer">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 Procurtal. All Rights Reserved.</p>
          </div>
        </footer>
        </>
        )}
        </>
        )}
      </div>
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} initialTab={authTab} />
    </div>
  );
}
